// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Pages & Components
import Login from './Login';
import Landing from './Landing';
import Inventory from './inventory/Inventory';
import Cart from './inventory/Cart';
import Logs from './inventory/Logs';
import Users from './Users';
import Customers from './customers/Customers';
import CustomerProfile from './customers/CustomerProfile';
import Contacts from './Contacts/Contacts';
import { loadGoogleMaps } from './lib/googlePlaces';
import Projects from './projects/Projects';
import ProjectProfile from './projects/ProjectProfile';
import SideNav from './SideNav';
import MobileNav from './MobileNav';
import Website from './settings/Website';
// Employees
import Employees from './employees/Employees';
import EmployeeProfile from './employees/EmployeeProfile';
import EmployeeScheduling from './employees/EmployeeScheduling';
import EmployeesTimecardList from './employees/EmployeesTimecardList'; // List to select employee
import EmployeeTimecard from './employees/EmployeeTimecard'; // Detailed timecard
import EmployeeMobileTimecard from './employees/EmployeeMobileTimecard';
// Billing
import BillingDashboard from './billing/BillingDashboard';
import CreateInvoice from './billing/CreateInvoice';
import CreateQuote from './billing/CreateQuote';
import CreateChangeOrder from './billing/CreateChangeOrder';
import CreateReceipt from './billing/CreateReceipt';
// Finances
import FinancialOverview from './finances/FinancialOverview';
import ProjectsFinances from './finances/ProjectsFinances';
import ExpenseLedger from './finances/ExpenseLedger';
import AuditTrail from './finances/AuditTrail';
import PriceBook from './finances/PriceBook';
// Contracts (new self-contained module under /src/contracts)
import Contract from './contracts/Contract';
// Settings
import CompanySettings from './settings/CompanySettings';
import PortalSettings from './settings/PortalSettings';
import ProfileSettings from './settings/ProfileSettings';
import CreateProposal from './billing/CreateProposal';
import { initPortalTheme } from './lib/portalTheme';
import UploadTray from './lib/UploadTray';
import axios from 'axios';
import { API_BASE } from './apiBase';



// ==================== GLOBAL SECURITY INTERCEPTOR ====================
// This catches expired 8-hour tokens on EVERY axios request and boots you to the login screen
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // A failed LOGIN attempt is also a 401 — but that one must NOT trigger
    // the boot-to-login reload, or the "wrong password / attempts left"
    // message gets wiped before the user can read it.
    const isLoginAttempt = error.config?.url?.includes('/login');
    if (error.response && error.response.status === 401 && !isLoginAttempt) {
      console.warn("Token expired! Booting to login screen...");
      localStorage.removeItem('token');
      localStorage.removeItem('role'); // Good practice to clear this too
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Is the stored token present AND not expired? Reading the expiry locally
// prevents the "flash": an expired token used to render the whole app for a
// few seconds until the first API call 401'd and booted you to login.
const isTokenAlive = () => {
  const t = localStorage.getItem('token');
  if (!t) return false;
  try {
    const payload = JSON.parse(atob(t.split('.')[1]));
    return !payload.exp || payload.exp * 1000 > Date.now();
  } catch (_) {
    return true; // unreadable token — let the server be the judge
  }
};

// ... inside your <Routes> ...
const ProtectedRoute = ({ children }) => {
  return isTokenAlive() ? children : <Navigate to="/login" replace />;
};
// Full protected layout: includes SideNav + main content margin.
// Desktop (md+): identical to before — fixed SideNav + 18rem content margin.
// Mobile (<md): SideNav hidden; MobileNav provides a top bar + drawer, and
// the content gets top padding to clear the bar instead of a left margin.
const ProtectedPortal = ({ children }) => {
  const location = useLocation();
  // Website mode hides the sidebar so the CMS gets the full viewport; its own
  // top bar carries the wordmark + Portal/Website picker instead.
  const fullBleed = location.pathname.startsWith('/website');
  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-950 flex overflow-hidden">
        {!fullBleed && <div className="hidden md:block"><SideNav /></div>}
        {!fullBleed && <MobileNav />}
        {/* max-md:overflow-x-hidden: phones must never scroll sideways at page level. */}
        <div className={`flex-1 bg-gray-950 ${fullBleed ? '' : 'md:ml-[18rem] max-md:pt-14 max-md:overflow-x-hidden'}`}>
          <main className="h-full w-full max-md:max-w-full">{children}</main>
        </div>
      </div>
    </ProtectedRoute>
  );
};

const SuperAdminRoute = ({ children }) => {
  const role = localStorage.getItem('role');
  // If they aren't a superadmin, gently bounce them to the Landing page
  return role === 'superadmin' ? children : <Navigate to="/" replace />;
};

// Employees never reach these pages (Billing, Employees, Customers, …) — the
// backend blocks the data too; this just keeps the UI honest.
const NoEmployeeRoute = ({ children }) => {
  const role = localStorage.getItem('role');
  return role === 'employee' ? <Navigate to="/" replace /> : children;
};

// Admin/Super-Admin gate. As a COMPONENT, the role is read on each render (at
// click-time), not baked once when the app first mounts — which is what broke
// the Timecards route when you logged in after the app had already loaded.
const AdminRoute = ({ children }) => {
  const role = localStorage.getItem('role');
  return ['admin', 'superadmin'].includes(role) ? children : <Navigate to="/employees" replace />;
};

// Contacts gate: admins and up, read at click time (see AdminRoute note).
// Non-admins land on the portal home rather than /employees.
const ContactsRoute = ({ children }) => {
  const role = localStorage.getItem('role');
  return ['admin', 'superadmin'].includes(role) ? children : <Navigate to="/" replace />;
};

// Website editor gate. Super admin always; a plain admin needs the websiteEditor
// permission (checked live via /me). Everyone else is bounced to the portal home.
const WebsiteRoute = ({ children }) => {
  const role = localStorage.getItem('role');
  const [allowed, setAllowed] = React.useState(role === 'superadmin' ? true : null);
  useEffect(() => {
    if (role === 'superadmin') return;
    if (role !== 'admin') { setAllowed(false); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/me`, { headers: { Authorization: token } })
      .then(res => setAllowed(res.data?.permissions?.websiteEditor === true))
      .catch(() => setAllowed(false));
  }, [role]);
  if (allowed === null) return null; // resolving permission
  return allowed ? children : <Navigate to="/" replace />;
};

function App() {

  // Load Google Maps/Places once, via the shared helper (key comes from
  // REACT_APP_GOOGLE_MAPS_KEY; see src/lib/googlePlaces.js)
  useEffect(() => {
    loadGoogleMaps().catch((e) => console.error('🚨 Google Maps failed to load:', e.message));
    initPortalTheme(); // paint maps from the cached theme, then sync server-side
  }, []);

  // Sliding session: every app launch trades the stored token for a fresh
  // 30-day one, so regular users never see a login screen — the session only
  // truly expires after 30 days of not opening the app at all.
  useEffect(() => {
    const t = localStorage.getItem('token');
    if (!t) return;
    axios.get(`${API_BASE}/validate-token`, { headers: { Authorization: t } })
      .then((r) => {
        if (r.data?.freshToken) localStorage.setItem('token', r.data.freshToken);
        if (r.data?.role) localStorage.setItem('role', r.data.role);
        if (r.data?.name) localStorage.setItem('name', r.data.name);
      })
      .catch(() => { /* 401 → the global interceptor already boots to login */ });
  }, []);

  return (
    <Router>

{/* App-wide upload pill: Docs & Media uploads keep running after you leave
    the page — this shows live progress + Cancel from anywhere. */}
<UploadTray />

<Routes>
{/* Public routes */}
<Route path="/login" element={<Login />} />
{/* Protected routes - all use ProtectedPortal for sidebar + auth */}
<Route path="/" element={<ProtectedPortal><Landing /></ProtectedPortal>} />
{/* Customers */}
<Route path="/customers" element={<ProtectedPortal><NoEmployeeRoute><Customers /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/customers/:id" element={<ProtectedPortal><NoEmployeeRoute><CustomerProfile /></NoEmployeeRoute></ProtectedPortal>} />
{/* Contacts (People / Relationships / Bids & Jobs) — admins and up.
    MUST be gated by a COMPONENT (ContactsRoute), never an inline expression:
    inline conditions are evaluated once at app boot — before login populates
    the role — and permanently bake in a redirect. A component re-reads the
    role at click time. (Same lesson as AdminRoute/Timecards.) */}
<Route path="/contacts" element={<ProtectedPortal><ContactsRoute><Contacts /></ContactsRoute></ProtectedPortal>} />
{/* Projects */}
<Route path="/projects" element={<ProtectedPortal><Projects /></ProtectedPortal>} />
<Route path="/projects/:id" element={<ProtectedPortal><ProjectProfile /></ProtectedPortal>} />
{/* Employees */}
<Route path="/employees" element={<ProtectedPortal><NoEmployeeRoute><Employees /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/employees/:id" element={<ProtectedPortal><NoEmployeeRoute><EmployeeProfile /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/employees/scheduling" element={<ProtectedPortal><NoEmployeeRoute><EmployeeScheduling /></NoEmployeeRoute></ProtectedPortal>} />
<Route
path="/employees/:id/mobile-timecard"
element={
<ProtectedPortal>
<EmployeeMobileTimecard />
</ProtectedPortal>
}
/>
{/* Timecards */}
<Route
path="/employees/timecards"
element={
<ProtectedPortal>
<AdminRoute><EmployeesTimecardList /></AdminRoute>
</ProtectedPortal>
}
/>
<Route
path="/employees/:id/timecard"
element={<ProtectedPortal><EmployeeTimecard /></ProtectedPortal>}
/>
{/* Inventory */}
<Route path="/inventory" element={<ProtectedPortal><Inventory /></ProtectedPortal>} />
<Route path="/inventory/logs" element={<ProtectedPortal><Logs /></ProtectedPortal>} />
<Route path="/inventory/cart" element={<ProtectedPortal><Cart /></ProtectedPortal>} />
{/* Billing */}
<Route path="/billing" element={<ProtectedPortal><NoEmployeeRoute><BillingDashboard /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/billing/create-invoice" element={<ProtectedPortal><NoEmployeeRoute><CreateInvoice /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/billing/create-quote" element={<ProtectedPortal><NoEmployeeRoute><CreateQuote /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/billing/create-proposal" element={<ProtectedPortal><NoEmployeeRoute><CreateProposal /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/billing/create-change-order" element={<ProtectedPortal><NoEmployeeRoute><CreateChangeOrder /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/billing/create-receipt" element={<ProtectedPortal><NoEmployeeRoute><CreateReceipt /></NoEmployeeRoute></ProtectedPortal>} />
<Route path="/billing/create-contract" element={<ProtectedPortal><NoEmployeeRoute><Contract /></NoEmployeeRoute></ProtectedPortal>} />
{/* ==================== FINANCES (Superadmin Only) ==================== */}
<Route
  path="/finances"
  element={
    <ProtectedPortal>
      <SuperAdminRoute>
        <FinancialOverview />
      </SuperAdminRoute>
    </ProtectedPortal>
  }
/>

<Route
  path="/finances/projects"
  element={
    <ProtectedPortal>
      <SuperAdminRoute>
        <ProjectsFinances />
      </SuperAdminRoute>
    </ProtectedPortal>
  }
/>

<Route
  path="/finances/ExpenseLedger"
  element={
    <ProtectedPortal>
      <SuperAdminRoute>
        <ExpenseLedger />
      </SuperAdminRoute>
    </ProtectedPortal>
  }
/>

<Route
  path="/finances/audit"
  element={
    <ProtectedPortal>
      <SuperAdminRoute>
        <AuditTrail />
      </SuperAdminRoute>
    </ProtectedPortal>
  }
/>

<Route
  path="/finances/price-book"
  element={
    <ProtectedPortal>
      <NoEmployeeRoute>
        <PriceBook />
      </NoEmployeeRoute>
    </ProtectedPortal>
  }
/>

{/* ==================== SETTINGS ==================== */}
<Route
  path="/settings/users"
  element={<ProtectedPortal><Users /></ProtectedPortal>}
/>
<Route
  path="/settings/company"
  element={<ProtectedPortal><SuperAdminRoute><CompanySettings /></SuperAdminRoute></ProtectedPortal>}
/>
<Route
  path="/settings/portal"
  element={<ProtectedPortal><SuperAdminRoute><PortalSettings /></SuperAdminRoute></ProtectedPortal>}
/>
<Route
  path="/settings/profile"
  element={<ProtectedPortal><ProfileSettings /></ProtectedPortal>}
/>

{/* Website editor — reached via the Portal/Website toggle in the sidenav (gated) */}
<Route path="/website" element={<ProtectedPortal><WebsiteRoute><Website /></WebsiteRoute></ProtectedPortal>} />

{/* Catch-all redirect */}
<Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

    </Router>
  );
}

export default App;